/**
 * Casino-table seat anchors for portrait multiplayer layout.
 * Local player is always offset 0 = bottom.
 * Opponents are placed from explicit per-count perimeter templates
 * (clockwise from local: right → top → left) so no two stations share a lane.
 */

/**
 * Deterministic slot order by total seated count.
 * Index = rotational offset from the local player (0 = bottom / local).
 */
export const SEAT_LAYOUTS_BY_COUNT = {
  1: ['bottom'],
  2: ['bottom', 'top'],
  3: ['bottom', 'right-mid', 'left-mid'],
  4: ['bottom', 'right-mid', 'top', 'left-mid'],
  // Four opponents: mid sides + split top — no double-stack on one rail
  5: ['bottom', 'right-mid', 'top-right', 'top-left', 'left-mid'],
  // Five opponents: two per side + center top
  6: ['bottom', 'right-lower', 'right-upper', 'top', 'left-upper', 'left-lower'],
  // Six opponents: distinct mid / upper / top-corner anchors (never 3-on-one-side)
  7: ['bottom', 'right-mid', 'right-upper', 'top-right', 'top-left', 'left-upper', 'left-mid'],
};

/**
 * Map absolute seat index → visual slot relative to local player.
 */
export function getSeatSlot(seatIndex, totalSeats, localPlayerIndex = 0) {
  const n = Math.max(Number(totalSeats) || 1, 1);
  const me = localPlayerIndex >= 0 ? localPlayerIndex : 0;
  const offset = ((seatIndex - me) % n + n) % n;

  if (SEAT_LAYOUTS_BY_COUNT[n]) {
    return SEAT_LAYOUTS_BY_COUNT[n][offset] || 'top';
  }

  // Rare 8+: keep a balanced 6-point ring (no 3-on-one-side prefix)
  if (offset === 0) return 'bottom';
  const ring = ['right-mid', 'right-upper', 'top-right', 'top', 'top-left', 'left-upper', 'left-mid'];
  return ring[(offset - 1) % ring.length];
}

/**
 * Percent anchors inside the table shell (rail-relative).
 * Values account for full station footprint (avatar + name + chips + badge),
 * not avatar centers alone. Side / top-corner lanes are staggered so
 * neighboring stations do not collide.
 */
export const SLOT_ANCHORS = {
  bottom: { left: 50, top: 91 },
  top: { left: 50, top: 8 },
  'top-left': { left: 30, top: 10 },
  'top-right': { left: 70, top: 10 },
  'left-mid': { left: 6, top: 52 },
  'right-mid': { left: 94, top: 52 },
  'left-upper': { left: 8, top: 30 },
  'left-lower': { left: 8, top: 74 },
  'right-upper': { left: 92, top: 30 },
  'right-lower': { left: 92, top: 74 },
  // legacy aliases
  left: { left: 6, top: 52 },
  right: { left: 94, top: 52 },
  'left-low': { left: 8, top: 74 },
  'right-low': { left: 92, top: 74 },
};

export function getHandAnchor(slot, isLocal) {
  if (isLocal || slot === 'bottom') {
    return { left: 50, top: 76 };
  }
  if (slot === 'top') return { left: 50, top: 20 };
  if (slot === 'top-left') return { left: 34, top: 21 };
  if (slot === 'top-right') return { left: 66, top: 21 };
  if (slot === 'left-upper') return { left: 23, top: 31 };
  if (slot === 'left-mid' || slot === 'left') return { left: 21, top: 52 };
  if (slot === 'left-lower' || slot === 'left-low') return { left: 23, top: 68 };
  if (slot === 'right-upper') return { left: 77, top: 31 };
  if (slot === 'right-mid' || slot === 'right') return { left: 79, top: 52 };
  if (slot === 'right-lower' || slot === 'right-low') return { left: 77, top: 68 };
  return { left: 50, top: 50 };
}

/**
 * Rough station bounding box in table-% for overlap checks.
 * Opponents are compact (~max-width 108px); local is larger.
 */
export function getStationFootprint(slot, isLocal = false) {
  const w = isLocal || slot === 'bottom' ? 28 : 22;
  const h = isLocal || slot === 'bottom' ? 22 : 18;
  return { width: w, height: h };
}

/**
 * True when two axis-aligned footprints overlap (inclusive margin in % points).
 */
export function stationsOverlap(slotA, slotB, margin = 1.5) {
  const a = SLOT_ANCHORS[slotA];
  const b = SLOT_ANCHORS[slotB];
  if (!a || !b || slotA === slotB) return false;
  const fa = getStationFootprint(slotA, slotA === 'bottom');
  const fb = getStationFootprint(slotB, slotB === 'bottom');
  const ax0 = a.left - fa.width / 2 - margin;
  const ax1 = a.left + fa.width / 2 + margin;
  const ay0 = a.top - fa.height / 2 - margin;
  const ay1 = a.top + fa.height / 2 + margin;
  const bx0 = b.left - fb.width / 2;
  const bx1 = b.left + fb.width / 2;
  const by0 = b.top - fb.height / 2;
  const by1 = b.top + fb.height / 2;
  return ax0 < bx1 && ax1 > bx0 && ay0 < by1 && ay1 > by0;
}

/**
 * Validate a seat-count layout: unique slots, no pairwise footprint overlap,
 * and local always bottom.
 */
export function validateSeatLayout(totalSeats) {
  const n = Math.max(Number(totalSeats) || 1, 1);
  const slots = Array.from({ length: n }, (_, i) => getSeatSlot(i, n, 0));
  const issues = [];
  if (slots[0] !== 'bottom') issues.push('local is not bottom');
  const uniq = new Set(slots);
  if (uniq.size !== slots.length) issues.push(`duplicate slots: ${slots.join(',')}`);
  for (let i = 0; i < slots.length; i += 1) {
    for (let j = i + 1; j < slots.length; j += 1) {
      if (stationsOverlap(slots[i], slots[j])) {
        issues.push(`overlap ${slots[i]} ↔ ${slots[j]}`);
      }
    }
  }
  // Keep clear of center pot band (~40–60 left, ~38–58 top)
  slots.filter((s) => s !== 'bottom').forEach((slot) => {
    const a = SLOT_ANCHORS[slot];
    if (a && a.left > 38 && a.left < 62 && a.top > 34 && a.top < 62) {
      issues.push(`${slot} intrudes center stage`);
    }
  });
  return { slots, ok: issues.length === 0, issues };
}

export function phaseLabel(phase) {
  switch (phase) {
    case 'waiting': return 'Waiting for players';
    case 'suitvote': return 'Vote for dealer suit';
    case 'dealer_reveal': return 'Finding dealer…';
    case 'dealer_selected': return 'Dealer selected';
    case 'deal': return 'Dealing cards…';
    case 'action': return 'Knock or discard';
    case 'reveal': return 'Revealing hands…';
    case 'results': return 'Hand complete';
    case 'done': return 'Hand complete';
    default: return phase || 'Gutz';
  }
}

/**
 * Derive a presentation-only winner summary from existing game state.
 * Does not recompute winners — only formats winnerIdx / notices.
 */
export function getHandResultSummary(gameState = {}, allSeats = []) {
  const phase = gameState.phase;
  const isResults = phase === 'results' || phase === 'done';

  if (gameState.allDiscardNotice) {
    return {
      kind: 'no_winner',
      title: 'No winner',
      subtitle: 'All players discarded',
      winners: [],
      earnings: 0,
      visible: true,
    };
  }

  if (gameState.showDealerNoFaceCardNotice) {
    return {
      kind: 'no_winner',
      title: 'No winner',
      subtitle: 'Dealer has no face card',
      winners: [],
      earnings: 0,
      visible: true,
    };
  }

  if (!isResults && !gameState.showDealerChangeNotice) {
    return { kind: 'none', title: '', subtitle: '', winners: [], earnings: 0, visible: false };
  }

  const raw = gameState.winnerIdx;
  const indices = Array.isArray(raw)
    ? raw.map(Number).filter((n) => Number.isFinite(n))
    : (raw != null && Number.isFinite(Number(raw)) ? [Number(raw)] : []);

  const winners = indices
    .map((idx) => allSeats[idx])
    .filter(Boolean)
    .map((p) => ({ id: p.playerID, name: p.name || 'Player' }));

  const earnings = Number(gameState.winnerEarnings || 0) || 0;
  const creditValue = Number(gameState.creditValue || 1) || 1;

  if (gameState.showDealerChangeNotice && gameState.willDealerChange) {
    const special = allSeats[gameState.dealerToBeIdx];
    return {
      kind: 'special',
      title: 'Special win!',
      subtitle: special?.name
        ? `${special.name} wins with a special hand`
        : 'Special hand — dealer will change',
      winners: special ? [{ id: special.playerID, name: special.name }] : winners,
      earnings,
      dollars: (earnings * creditValue).toFixed(2),
      visible: true,
    };
  }

  if (!winners.length) {
    return {
      kind: 'no_winner',
      title: 'No winner',
      subtitle: 'Hand complete — pot carries or settles per table rules',
      winners: [],
      earnings: 0,
      visible: isResults,
    };
  }

  const names = winners.map((w) => w.name).join(' & ');
  return {
    kind: winners.length > 1 ? 'tie' : 'win',
    title: winners.length > 1 ? 'Tie!' : 'Winner!',
    subtitle: winners.length > 1 ? `${names} split the pot` : `${names} wins`,
    winners,
    earnings,
    dollars: (earnings * creditValue).toFixed(2),
    visible: true,
  };
}
